import React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import SVG from "@/assets/svg";
import logger from "@/runtime-utils/observability/logger";

interface ListViewProps<T> {
  data: T[];
  loading?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
  loader?: React.ReactNode;
  emptyComponent?: React.ReactNode;
}

export const EmptyComponent = ({ label }: { label?: string }) => (
  <Box
    className="loading-container"
    style={{ height: "290px", alignItems: "center" }}
  >
    <Box
      sx={{
        fontSize: "16px",
        fontWeight: "600",
        color: "#1F4970",
        margin: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <SVG.File />
      {label || "No data found"}
    </Box>
  </Box>
);

const DefaultLoader = () => (
  <Box className="loading-container" style={{ height: "30vh" }}>
    <CircularProgress />
  </Box>
);

const ListView = <T,>({
  data = [],
  loading,
  renderItem,
  loader = <DefaultLoader />,
  emptyComponent = <EmptyComponent />,
}: ListViewProps<T>) => {
  if (loading && loader) {
    return <React.Fragment>{loader}</React.Fragment>;
  }

  if (!Array.isArray(data)) {
    logger.error("The `data` prop is not an array:", data);
    return <React.Fragment>{emptyComponent}</React.Fragment>;
  }

  if (data.length === 0) {
    return <React.Fragment>{emptyComponent}</React.Fragment>;
  }

  return (
    <>
      {data.map((item, index) => (
        <React.Fragment key={index}>{renderItem(item, index)}</React.Fragment>
      ))}
    </>
  );
};

export default ListView;
